/**
 * wikiSanitise.js - the wiki HTML sanitisation policy: allowlist, link hook and
 * the sanitise call itself. Wiki pages are admin-authored HTML rendered with
 * dangerouslySetInnerHTML, so they are sanitised on the way in (every write
 * path) and on the way out (the client render).
 *
 * This file exists in TWO byte-identical copies:
 *   lambda/layer/nodejs/wikiSanitise.js   (backend, via /opt/nodejs/wikiSanitise)
 *   frontend/src/utils/wikiSanitise.js    (frontend)
 * A jest test asserts the copies are identical - edit BOTH together.
 *
 * The whole policy lives here, not just its configuration: one definition of
 * what wiki HTML may contain, used unchanged by the Lambda layer and by the
 * client bundle. isomorphic-dompurify resolves to jsdom under Node and to plain
 * DOMPurify in a browser build, which is what lets one module serve both.
 */

const DOMPurify = require('isomorphic-dompurify');

// Matches what the TipTap editor emits (WikiPageEditor.js) rather than the
// DOMPurify default: every tag the wiki can legitimately produce, nothing else.
const ALLOWED_TAGS = [
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'p', 'br', 'hr', 'div', 'span',
    'ul', 'ol', 'li',
    'blockquote', 'pre', 'code',
    'strong', 'em', 'u', 's', 'mark',
    'a', 'img',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    // colgroup/col carry per-column widths for a resizable table. Dropping them
    // silently reset every column to equal width on save.
    'colgroup', 'col'
];

// style is required: the image resize feature (ResizableImage in
// WikiPageEditor.js) writes inline width/height there, so dropping it would
// silently reformat every existing page.
const ALLOWED_ATTR = [
    'href', 'src', 'alt', 'title',
    'width', 'height', 'style', 'class',
    'colspan', 'rowspan',
    'target', 'rel'
];

// https:, mailto: and relative paths only - javascript: cannot match. http: is
// deliberately excluded (decision 2026-07-30): such links keep their text and
// lose their href. The two trailing alternatives are the DOMPurify idiom for
// relative URLs - anything not starting with a letter, and letter-led paths
// carrying no colon.
const ALLOWED_URI_REGEXP = /^(?:(?:https|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.:-]|$))/i;

// URL-bearing attributes, and the whitespace stripped before a URI is tested -
// a scheme can be split with control characters. Browsers only ignore ASCII
// whitespace inside a URL, so this class covers every real bypass: a scheme
// split with a zero-width character is not a scheme to the browser either.
const URI_ATTRS = ['src', 'href', 'xlink:href'];
// eslint-disable-next-line no-control-regex -- control characters are the point
const ATTR_WHITESPACE = /[\s\x00-\x20]/g;

// CSS properties the wiki editor emits, and nothing else: image and table
// column widths (ResizableImage, col), text colour (Color) and highlight
// (Highlight multicolor). DOMPurify does not filter style CONTENTS, so an
// unfiltered property is an exfiltration or overlay channel - background:
// url(...) is a tracking beacon and position: fixed is a click-jacking
// surface. Everything not named here is stripped (D2, 2026-08-30).
//
// Values are validated PER PROPERTY (F6): one shared pattern let
// `color: expression(alert(1))` through. These shapes exclude expression(),
// url() and unbounded sizes structurally.
//
// ⚠️ This list was confirmed against the LIVE CORPUS before it was narrowed
// (scripts/report-wiki-style-usage.js, run 2026-08-30), because the sanitiser
// runs on the READ path too: anything missing here disappears from existing
// client wikis at render, silently. That run is what added min-width.
const ALLOWED_STYLE_PROPS = {
    'width': /^(auto|inherit|\d{1,5}(\.\d+)?(px|%|em|rem))$/,
    // min-width: the editor writes it on every un-resized table column
    // (corpus report 2026-08-30: 18 occurrences on live pages - D7).
    'min-width': /^(auto|inherit|\d{1,5}(\.\d+)?(px|%|em|rem))$/,
    'height': /^(auto|inherit|\d{1,5}(\.\d+)?(px|%|em|rem))$/,
    'color': /^(inherit|transparent|#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|[a-z]{3,20})$/,
    'background-color': /^(inherit|transparent|#[0-9a-fA-F]{3,8}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\)|[a-z]{3,20})$/
};

function filterStyleAttribute(node) {
    if (!node.hasAttribute('style')) return;
    const kept = [];
    for (const decl of node.getAttribute('style').split(';')) {
        const idx = decl.indexOf(':');
        if (idx === -1) continue;
        const prop = decl.slice(0, idx).trim().toLowerCase();
        const value = decl.slice(idx + 1).trim();
        if (ALLOWED_STYLE_PROPS[prop] && ALLOWED_STYLE_PROPS[prop].test(value)) {
            kept.push(`${prop}: ${value}`);
        }
    }
    if (kept.length > 0) {
        node.setAttribute('style', kept.join('; '));
    } else {
        node.removeAttribute('style');
    }
}

// class values the wiki editor itself emits (WikiPageEditor.js Link,
// CodeBlock, Blockquote configs) - exact strings, extended only from the
// scripts/report-wiki-style-usage.js corpus report. NEVER token-level: the
// wiki renders inside the Tailwind bundle, so arbitrary tokens are a layout
// weapon (class="fixed inset-0 z-50" covers the portal).
const ALLOWED_CLASS_VALUES = new Set([
    'text-primary-600 underline hover:text-primary-800',
    'bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto',
    'border-l-4 border-gray-300 pl-4 italic text-gray-600'
]);

function filterClassAttribute(node) {
    if (!node.hasAttribute('class')) return;
    if (!ALLOWED_CLASS_VALUES.has(node.getAttribute('class'))) {
        node.removeAttribute('class');
    }
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!node.hasAttribute) return;

    // A link opened in a new tab must not hand its opener window to the target
    // page (tab-nabbing). TipTap writes rel itself, but HTML pasted into the
    // editor carries no such guarantee, so it is forced here for any target.
    if (node.hasAttribute('target')) {
        node.setAttribute('rel', 'noopener noreferrer');
    }

    // DOMPurify allows data: URIs on img/audio/video/source/track regardless of
    // ALLOWED_URI_REGEXP - its DATA_URI_TAGS carve-out, which no config option
    // empties. This policy admits https:, mailto: and relative paths only, so
    // the carve-out is undone here.
    URI_ATTRS.forEach((attr) => {
        if (!node.hasAttribute(attr)) return;
        const value = node.getAttribute(attr).replace(ATTR_WHITESPACE, '');
        if (/^data:/i.test(value)) {
            node.removeAttribute(attr);
        }
    });

    filterStyleAttribute(node);
    filterClassAttribute(node);
});

/**
 * Sanitise a wiki HTML string. Returns '' for empty input so callers can store
 * the result directly.
 */
function sanitiseWikiHtml(html) {
    if (!html) return '';

    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR,
        ALLOWED_URI_REGEXP
    });
}

module.exports = {
    sanitiseWikiHtml,
    // Exported for the parity test that keeps this set in step with the
    // classes WikiPageEditor.js configures - restyle a link there without
    // updating this and the sanitiser silently strips the new class from
    // every newly-saved page, on the read path, with nothing failing.
    ALLOWED_CLASS_VALUES
};
